import { useState, useEffect, useCallback } from "react";
import { portalClienteImplantacao, portalClienteAtualizarEtapa } from "../api/functions";

const BRAND = { primary: "#0FC9C5", card: "#1e293b", border: "#334155", bg: "#0f172a" };

const STATUS_META = {
  "Não iniciada": { icon: "⚪", color: "#94a3b8" },
  "Em andamento": { icon: "🔵", color: "#60a5fa" },
  "Concluída": { icon: "✅", color: "#34d399" },
  "Atrasada": { icon: "🔴", color: "#f87171" },
  "Pausada": { icon: "🟡", color: "#fbbf24" },
  "Bloqueada": { icon: "🔒", color: "#fb923c" }
};

const FASES_ORDEM = [
  "Preparação e Documentação",
  "Instalação e Configuração",
  "Agente de IA",
  "Validação",
  "Capacitação e Go-live",
  "Funcionalidades Avançadas",
  "Integração com Banco de Dados",
  "Sem fase"
];

const fmt = (iso) => {
  if (!iso) return null;
  const p = iso.slice(0, 10).split("-");
  return `${p[2]}/${p[1]}/${p[0]}`;
};

export default function Implantacao({ usuario }) {
  const [implantacao, setImplantacao] = useState(null);
  const [etapas, setEtapas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [semImplantacao, setSemImplantacao] = useState(false);
  const [msg, setMsg] = useState(null); // { tipo: 'ok'|'erro', texto }
  const [carregandoEtapa, setCarregandoEtapa] = useState(null);
  const [etapaAberta, setEtapaAberta] = useState(null); // id da etapa com ações expandidas

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      const r = await portalClienteImplantacao({ empresa_id: usuario.empresa_id, usuario_id: usuario.id });
      if (!r.success) {
        setSemImplantacao(true);
        setMsg({ tipo: "erro", texto: r.error || "Não foi possível carregar a implantação." });
        return;
      }
      if (!r.implantacao) {
        setSemImplantacao(true);
        return;
      }
      setImplantacao(r.implantacao);
      setEtapas(r.etapas || []);
    } catch (e) {
      setMsg({ tipo: "erro", texto: "Erro de conexão. Tente novamente." });
    } finally {
      setLoading(false);
    }
  }, [usuario.empresa_id, usuario.id]);

  useEffect(() => { carregar(); }, [carregar]);

  const agir = async (etapa, acao, extra) => {
    setCarregandoEtapa(etapa.id);
    setMsg(null);
    try {
      const r = await portalClienteAtualizarEtapa({
        empresa_id: usuario.empresa_id,
        usuario_id: usuario.id,
        etapa_id: etapa.id,
        acao,
        ...extra
      });
      if (!r.success) {
        setMsg({ tipo: "erro", texto: r.error || "Não foi possível atualizar." });
      } else {
        setMsg({ tipo: "ok", texto: acao === "concluir" ? "Etapa concluída! 🎉" : acao === "definir_data" ? "Data definida com sucesso." : "Observação registrada." });
        await carregar();
      }
    } catch (e) {
      setMsg({ tipo: "erro", texto: "Erro de conexão. Tente novamente." });
    } finally {
      setCarregandoEtapa(null);
    }
  };

  if (loading) {
    return (
      <div style={{ color: "#64748b", padding: "60px 0", textAlign: "center", fontSize: "14px" }}>
        ⏳ Carregando implantação...
      </div>
    );
  }

  if (semImplantacao) {
    return (
      <div style={{ background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "16px", padding: "48px 24px", textAlign: "center" }}>
        <div style={{ fontSize: "40px", marginBottom: "12px" }}>📋</div>
        <div style={{ color: "#f1f5f9", fontWeight: "600", fontSize: "16px", marginBottom: "6px" }}>Nenhuma implantação em andamento</div>
        <div style={{ color: "#64748b", fontSize: "13px" }}>Quando sua implantação for iniciada, o checklist completo aparecerá aqui.</div>
      </div>
    );
  }

  const concluidas = etapas.filter(e => e.status === "Concluída").length;
  const pctGeral = etapas.length ? Math.round((concluidas / etapas.length) * 100) : 0;

  // Visão geral: fase atual, próxima etapa, pendências do cliente
  const abertas = etapas.filter(e => e.status !== "Concluída");
  const proximaEtapa = abertas[0] || null;
  const faseAtual = proximaEtapa ? (proximaEtapa.fase || "Sem fase") : null;
  const minhasPendencias = abertas.filter(e => e.responsavel_execucao === "cliente");

  const grupos = {};
  for (const e of etapas) {
    const f = e.fase || "Sem fase";
    (grupos[f] = grupos[f] || []).push(e);
  }
  const fases = FASES_ORDEM.filter(f => grupos[f]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {msg && (
        <div style={{
          background: msg.tipo === "ok" ? "#34d39922" : "#f8717122",
          border: `1px solid ${msg.tipo === "ok" ? "#34d39955" : "#f8717155"}`,
          color: msg.tipo === "ok" ? "#34d399" : "#f87171",
          borderRadius: "10px", padding: "10px 16px", fontSize: "13px"
        }}>{msg.texto}</div>
      )}

      {/* Cabeçalho do projeto */}
      <div style={{ background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "16px", padding: "20px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Sua Implantação</div>
            <div style={{ fontSize: "20px", fontWeight: "800", color: "#f1f5f9" }}>{implantacao.nome_projeto}</div>
            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "6px" }}>
              {implantacao.consultor_responsavel_nome || implantacao.gerente_projeto_nome
                ? `Responsável: ${implantacao.consultor_responsavel_nome || implantacao.gerente_projeto_nome}` : null}
            </div>
          </div>
          <span style={{ background: BRAND.primary + "20", color: BRAND.primary, fontSize: "12px", fontWeight: "600", padding: "4px 12px", borderRadius: "20px", border: `1px solid ${BRAND.primary}40` }}>
            {implantacao.status}
          </span>
        </div>
        <div style={{ marginTop: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#64748b", marginBottom: "6px" }}>
            <span>Progresso geral</span>
            <span style={{ color: BRAND.primary, fontWeight: "700" }}>{pctGeral}% · {concluidas}/{etapas.length} etapas</span>
          </div>
          <div style={{ background: "#0f172a", borderRadius: "10px", height: "10px", overflow: "hidden" }}>
            <div style={{ background: `linear-gradient(90deg, ${BRAND.primary}, #34d399)`, width: `${pctGeral}%`, height: "100%", borderRadius: "10px", transition: "width 0.4s" }} />
          </div>
        </div>
      </div>

      {/* Visão geral */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ flex: 1, minWidth: "220px", background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "14px", padding: "16px 18px" }}>
          <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "6px" }}>📍 ONDE ESTAMOS</div>
          <div style={{ fontSize: "15px", fontWeight: "700", color: "#f1f5f9" }}>
            {faseAtual ? (faseAtual === "Sem fase" ? "Em andamento" : faseAtual) : "Implantação concluída! 🎉"}
          </div>
          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
            {proximaEtapa ? `Etapa ${proximaEtapa.ordem} de ${etapas.length}` : `Todas as ${etapas.length} etapas finalizadas`}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: "220px", background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "14px", padding: "16px 18px" }}>
          <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "6px" }}>⏭️ PRÓXIMA ETAPA</div>
          <div style={{ fontSize: "14px", fontWeight: "600", color: "#f1f5f9" }}>
            {proximaEtapa ? proximaEtapa.nome_etapa : "Nenhuma pendência restante"}
          </div>
          {proximaEtapa && (
            <div style={{ marginTop: "6px" }}>
              <span style={{ background: proximaEtapa.responsavel_execucao === "cliente" ? "#fb923c22" : "#94a3b822", color: proximaEtapa.responsavel_execucao === "cliente" ? "#fb923c" : "#94a3b8", fontSize: "10px", fontWeight: "600", padding: "2px 8px", borderRadius: "12px", border: proximaEtapa.responsavel_execucao === "cliente" ? "1px solid #fb923c44" : "none" }}>
                {proximaEtapa.responsavel_execucao === "cliente" ? "Sua ação" : "Nossa equipe"}
              </span>
              {proximaEtapa.data_fim_planejada && (
                <span style={{ fontSize: "11px", color: "#64748b", marginLeft: "6px" }}>📅 {fmt(proximaEtapa.data_fim_planejada)}</span>
              )}
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: "220px", background: minhasPendencias.length > 0 ? "#fb923c18" : BRAND.card, border: `1px solid ${minhasPendencias.length > 0 ? "#fb923c55" : BRAND.border}`, borderRadius: "14px", padding: "16px 18px" }}>
          <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "6px" }}>✅ SUAS PENDÊNCIAS</div>
          <div style={{ fontSize: "15px", fontWeight: "700", color: minhasPendencias.length > 0 ? "#fb923c" : "#34d399" }}>
            {minhasPendencias.length > 0 ? `${minhasPendencias.length} etapa${minhasPendencias.length > 1 ? "s" : ""} esperando você` : "Tudo em dia!"}
          </div>
          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
            {minhasPendencias.length > 0 ? minhasPendencias[0].nome_etapa : "Nenhuma ação sua pendente"}
          </div>
        </div>
      </div>

      {/* Fases */}
      {fases.map(fase => {
        const lista = grupos[fase];
        const ok = lista.filter(e => e.status === "Concluída").length;
        const pct = Math.round((ok / lista.length) * 100);
        return (
          <div key={fase} style={{ background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "16px", overflow: "hidden" }}>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${BRAND.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
              <div>
                <div style={{ fontWeight: "700", fontSize: "14px", color: "#f1f5f9" }}>{fase === "Sem fase" ? "Outras etapas" : fase}</div>
                <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>{ok} de {lista.length} concluídas</div>
              </div>
              <span style={{ color: BRAND.primary, fontWeight: "700", fontSize: "13px" }}>{pct}%</span>
            </div>
            <div>
              {lista.map((etapa, idx) => (
                <EtapaItem
                  key={etapa.id}
                  etapa={etapa}
                  ultima={idx === lista.length - 1}
                  aberta={etapaAberta === etapa.id}
                  onToggle={() => setEtapaAberta(etapaAberta === etapa.id ? null : etapa.id)}
                  onAgir={agir}
                  carregando={carregandoEtapa === etapa.id}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EtapaItem({ etapa, ultima, aberta, onToggle, onAgir, carregando }) {
  const meta = STATUS_META[etapa.status] || STATUS_META["Não iniciada"];
  const doCliente = etapa.responsavel_execucao === "cliente";
  const encerrada = etapa.status === "Concluída" || etapa.status === "Cancelada";
  const [data, setData] = useState("");
  const [obs, setObs] = useState("");

  return (
    <div style={{ padding: "12px 20px", borderBottom: ultima ? "none" : `1px solid ${BRAND.border}33` }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <span style={{ fontSize: "15px", lineHeight: "20px" }}>{meta.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
            <span style={{ color: encerrada ? "#64748b" : "#f1f5f9", fontSize: "13px", fontWeight: "500", textDecoration: encerrada ? "line-through" : "none" }}>
              {etapa.nome_etapa}
            </span>
            {doCliente ? (
              <span style={{ background: "#fb923c22", color: "#fb923c", fontSize: "10px", fontWeight: "600", padding: "2px 8px", borderRadius: "12px", border: "1px solid #fb923c44" }}>
                Sua ação
              </span>
            ) : (
              <span style={{ background: "#94a3b822", color: "#94a3b8", fontSize: "10px", fontWeight: "600", padding: "2px 8px", borderRadius: "12px" }}>
                Nossa equipe
              </span>
            )}
          </div>
          {etapa.descricao && <div style={{ color: "#64748b", fontSize: "12px", marginTop: "3px" }}>{etapa.descricao}</div>}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginTop: "4px", fontSize: "11px", color: "#64748b" }}>
            {etapa.data_fim_planejada && <span>📅 Previsão: {fmt(etapa.data_fim_planejada)}</span>}
            {etapa.data_fim_real && <span style={{ color: "#34d399" }}>✅ Concluída em {fmt(etapa.data_fim_real)}</span>}
          </div>
          {etapa.observacoes && (
            <div style={{ marginTop: "6px", background: "#0f172a", borderRadius: "8px", padding: "8px 10px", fontSize: "11px", color: "#94a3b8", whiteSpace: "pre-wrap" }}>
              {etapa.observacoes}
            </div>
          )}
          {etapa.status === "Bloqueada" && (
            <div style={{ marginTop: "6px", background: "#fb923c18", border: "1px solid #fb923c44", borderRadius: "8px", padding: "8px 10px", fontSize: "11px", color: "#fb923c" }}>
              🔒 Etapa bloqueada — consulte o motivo nas observações ou fale com o nosso time
            </div>
          )}
          {etapa.criterio_aceite && (
            <div style={{ marginTop: "6px", fontSize: "11px", color: "#94a3b8" }}>
              <span style={{ color: BRAND.primary, fontWeight: "600" }}>🎯 Critério de aceite:</span> {etapa.criterio_aceite}
            </div>
          )}
          {etapa.evidencia && (
            <div style={{ marginTop: "4px", fontSize: "11px", color: "#94a3b8" }}>
              <span style={{ color: "#34d399", fontWeight: "600" }}>📎 Evidência:</span> {etapa.evidencia}
            </div>
          )}
        </div>
        {doCliente && !encerrada && (
          <button onClick={onToggle} disabled={carregando} style={{
            background: aberta ? BRAND.primary : "transparent",
            border: `1px solid ${aberta ? BRAND.primary : BRAND.border}`,
            color: aberta ? "#0f172a" : BRAND.primary,
            fontSize: "11px", fontWeight: "600", padding: "5px 10px", borderRadius: "8px",
            cursor: "pointer", whiteSpace: "nowrap"
          }}>
            {aberta ? "Fechar" : "Atualizar"}
          </button>
        )}
      </div>

      {aberta && doCliente && !encerrada && (
        <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px", background: "#0f172a", borderRadius: "10px", padding: "12px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
            <span style={{ color: "#94a3b8", fontSize: "12px" }}>Quando pretende concluir?</span>
            <input
              type="date"
              value={data}
              onChange={e => setData(e.target.value)}
              style={{ background: BRAND.card, border: `1px solid ${BRAND.border}`, color: "#f1f5f9", borderRadius: "8px", padding: "6px 10px", fontSize: "12px" }}
            />
            <button
              onClick={() => data && onAgir(etapa, "definir_data", { data_planejada: data })}
              disabled={carregando || !data}
              style={{ background: BRAND.card, border: `1px solid ${BRAND.border}`, color: "#f1f5f9", fontSize: "12px", padding: "6px 12px", borderRadius: "8px", cursor: carregando || !data ? "default" : "pointer", opacity: !data ? 0.5 : 1 }}
            >Definir data</button>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              type="text"
              value={obs}
              onChange={e => setObs(e.target.value)}
              placeholder="Adicionar observação para nossa equipe..."
              maxLength={500}
              style={{ flex: 1, background: BRAND.card, border: `1px solid ${BRAND.border}`, color: "#f1f5f9", borderRadius: "8px", padding: "6px 10px", fontSize: "12px" }}
            />
            <button
              onClick={() => obs.trim() && onAgir(etapa, "observar", { observacao: obs.trim() }) && setObs("")}
              disabled={carregando || !obs.trim()}
              style={{ background: BRAND.card, border: `1px solid ${BRAND.border}`, color: "#f1f5f9", fontSize: "12px", padding: "6px 12px", borderRadius: "8px", cursor: carregando || !obs.trim() ? "default" : "pointer", opacity: !obs.trim() ? 0.5 : 1 }}
            >Enviar</button>
          </div>
          <button
            onClick={() => onAgir(etapa, "concluir")}
            disabled={carregando}
            style={{ background: BRAND.primary, border: "none", color: "#0f172a", fontSize: "13px", fontWeight: "700", padding: "10px", borderRadius: "10px", cursor: carregando ? "default" : "pointer", opacity: carregando ? 0.6 : 1 }}
          >✅ Marcar como concluída</button>
        </div>
      )}
    </div>
  );
}
